import { useState, useEffect, useRef, useMemo } from "react";
import {
  Box, Button, Input, Textarea, Stack, Typography,
  Autocomplete, AutocompleteOption, ListItemContent, CircularProgress,
} from "@mui/joy";
import { motion } from "framer-motion";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";

import { Section, Container, COLORS, GlowOrbs } from "../shared/ui";
import { APIClientCreateShortApplication, APISearchCities } from "../api";
import MaskedInput from "../components/JoyMaskedInput";

const PROMISES = [
  "Перезвоним в течение 5 минут",
  "Точная цена по телефону — без сюрпризов",
  "Выезд уже на следующий день",
];

/* ── Автокомплит города (тёмная тема) ── */

const makeAddNewCityOptionDark = (cityName) => ({
  id: '__NEW__',
  name: cityName,
  isNew: true,
});

function CityAutocompleteDark({ value, customCityName, onChange, onCustomChange }) {
  const [options, setOptions] = useState([]);
  const [initialCities, setInitialCities] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const searchTimer = useRef(null);

  // Начальные города при монтировании
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const cities = await APISearchCities('', 15);
      if (!cancelled) {
        setInitialCities(Array.isArray(cities) ? cities : []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Поиск при вводе
  useEffect(() => {
    if (!inputValue || inputValue.length < 2) {
      setOptions([]);
      return;
    }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setLoading(true);
      const results = await APISearchCities(inputValue, 15);
      setOptions(Array.isArray(results) ? results : []);
      setLoading(false);
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [inputValue]);

  const displayOptions = useMemo(() => {
    const query = (inputValue || '').trim();
    const hasQuery = query.length >= 2;

    let base = hasQuery && options.length > 0 ? options : initialCities;
    base = Array.isArray(base) ? base : [];

    if (hasQuery) {
      const exactMatch = base.some(c =>
        (c?.name || '').toLowerCase().trim() === query.toLowerCase()
      );
      if (!exactMatch) {
        return [...base, makeAddNewCityOptionDark(query)];
      }
    }
    return base;
  }, [options, initialCities, inputValue]);

  const selectedCity = (() => {
    if (!value && customCityName) {
      return makeAddNewCityOptionDark(customCityName);
    }
    if (!value) return null;
    return (
      displayOptions.find(c => c.id === value)
      || initialCities.find(c => c.id === value)
      || null
    );
  })();

  return (
    <Autocomplete
      placeholder="Введите город или выберите"
      value={selectedCity}
      onChange={(_, newVal) => {
        if (!newVal) {
          onChange(null);
          onCustomChange?.('');
          return;
        }
        if (newVal.id === '__NEW__') {
          onChange(null);
          onCustomChange?.(newVal.name);
          return;
        }
        onChange(newVal.id);
        onCustomChange?.('');
      }}
      inputValue={inputValue}
      onInputChange={(_, val) => setInputValue(val)}
      options={displayOptions}
      getOptionLabel={(opt) => opt?.name || ''}
      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
      loading={loading}
      noOptionsText={inputValue ? "Загрузка..." : "Начните вводить название"}
      startDecorator={<SearchRoundedIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.4)' }} />}
      endDecorator={loading ? <CircularProgress size="sm" sx={{ mr: 1 }} /> : null}
      slotProps={{ listbox: { sx: { maxHeight: 220 } } }}
      renderOption={(props, option) => {
        const isNewInline = option.id === '__NEW__';
        const sx = isNewInline
          ? { borderTop: '1px solid', borderColor: 'divider', bgcolor: 'primary.50', '&:hover': { bgcolor: 'primary.100' } }
          : {};
        return (
          <AutocompleteOption {...props} key={option.id} sx={sx}>
            <ListItemContent>
              {isNewInline ? (
                <Box>
                  <Typography level="body-sm" sx={{ fontWeight: 700, color: 'primary.700' }}>
                    + Использовать «{option.name}» как новый город
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.25 }}>
                    💡 Менеджер проверит и добавит в базу
                  </Typography>
                </Box>
              ) : (
                <Typography level="body-sm" fontWeight={600}>{option.name}</Typography>
              )}
            </ListItemContent>
          </AutocompleteOption>
        );
      }}
      sx={{
        ...fieldSx,
        '& .MuiAutocomplete-input': { fontSize: '0.875rem', color: '#fff' },
      }}
    />
  );
}


export default function CTASection() {
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState(null);
  const [customCityName, setCustomCityName] = useState("");
  const [desiredDate, setDesiredDate] = useState("");
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (loading) return;
    if (!phone.trim()) {
      setError("Заполните телефон");
      return;
    }
    setError("");
    setLoading(true);

    const payload = {
      phone,
      comment,
      city: city || null,
      desired_date: desiredDate || null,
    };
    if (!city && customCityName.trim()) {
      payload.custom_city_name = customCityName.trim();
    }

    try {
      const res = await APIClientCreateShortApplication(payload);
      if (res?.status === "error") {
        setError(typeof res.detail === "string" ? res.detail : "Не удалось отправить заявку");
        return;
      }
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section
      id="cta"
      dark
      py={{ xs: 10, md: 14 }}
      sx={{ background: `linear-gradient(135deg, ${COLORS.dark} 0%, #0f1630 100%)` }}
    >
      <GlowOrbs />

      <Container maxWidth={1100}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 5, md: 6 },
            alignItems: "center",
          }}
        >
          {/* Левая колонка — текст */}
          <Box>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5 }}
            >
              <Typography
                level="body-xs"
                sx={{
                  color: COLORS.brandLight,
                  textTransform: "uppercase",
                  letterSpacing: "0.16em",
                  fontWeight: 700,
                  mb: 1.5,
                }}
              >
                Оставьте заявку
              </Typography>
              <Typography
                level="h2"
                sx={{
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: { xs: "2rem", md: "3rem" },
                  letterSpacing: "-1.5px",
                  lineHeight: 1.05,
                  mb: 2.5,
                }}
              >
                Получите обмерный план{" "}
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(135deg, #818cf8, #c084fc)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  за 3 дня
                </Box>
              </Typography>
              <Typography
                level="body-lg"
                sx={{ color: COLORS.textDim, mb: 4, lineHeight: 1.6, maxWidth: 480 }}
              >
                Заполните форму — менеджер рассчитает точную стоимость и согласует дату замера.
              </Typography>

              <Stack spacing={1.5}>
                {PROMISES.map((p) => (
                  <Stack key={p} direction="row" spacing={1.25} alignItems="center">
                    <CheckCircleRoundedIcon sx={{ color: "#34d399", fontSize: 22 }} />
                    <Typography sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 500 }}>
                      {p}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </motion.div>
          </Box>

          {/* Правая колонка — форма */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: 0.1 }}
          >
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: "24px",
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.12)",
                backdropFilter: "blur(20px)",
                boxShadow: "0 30px 80px rgba(0,0,0,0.3)",
              }}
            >
              {sent ? (
                <SuccessState />
              ) : (
                <Stack spacing={2}>
                  {/* Телефон */}
                  <FormField label="Телефон">
                    <MaskedInput
                      mask="+7 (000) 000-00-00"
                      placeholder="+7 (000) 000-00-00"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      size="lg"
                      sx={fieldSx}
                    />
                  </FormField>

                  {/* Город */}
                  <FormField label="Город">
                    <CityAutocompleteDark
                      value={city}
                      customCityName={customCityName}
                      onChange={(id) => { setCity(id); if (id) setCustomCityName(""); }}
                      onCustomChange={(name) => { setCustomCityName(name); if (name) setCity(null); }}
                    />
                  </FormField>

                  {/* Дата и комментарий в ряд на десктопе */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <FormField label="Желаемая дата">
                        <Input
                          type="date"
                          value={desiredDate}
                          onChange={(e) => setDesiredDate(e.target.value)}
                          startDecorator={
                            <CalendarMonthRoundedIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.4)' }} />
                          }
                          sx={fieldSx}
                          slotProps={{
                            input: { min: new Date().toISOString().split("T")[0] },
                          }}
                        />
                      </FormField>
                    </Box>
                  </Stack>

                  {/* Комментарий */}
                  <FormField label="Комментарий">
                    <Textarea
                      placeholder="Опишите ваш объект или пожелания"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      minRows={2}
                      maxRows={4}
                      sx={{
                        ...fieldSx,
                        '& textarea::placeholder': { color: 'rgba(255,255,255,0.35)' },
                      }}
                    />
                  </FormField>

                  {error && (
                    <Typography level="body-sm" sx={{ color: "#f87171" }}>
                      {error}
                    </Typography>
                  )}

                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      fullWidth
                      size="lg"
                      onClick={submit}
                      loading={loading}
                      endDecorator={<SendRoundedIcon />}
                      sx={{
                        fontWeight: 800,
                        py: 1.75,
                        mt: 1,
                        borderRadius: "14px",
                        fontSize: "1rem",
                        background: `linear-gradient(135deg, ${COLORS.brand}, ${COLORS.brand2})`,
                        boxShadow: "0 10px 32px rgba(99,102,241,0.5)",
                        "&:hover": {
                          boxShadow: "0 14px 44px rgba(99,102,241,0.7)",
                          background: `linear-gradient(135deg, ${COLORS.brand}, ${COLORS.brand2})`,
                        },
                      }}
                    >
                      Оставить заявку
                    </Button>
                  </motion.div>

                  <Typography
                    level="body-xs"
                    sx={{ color: COLORS.textMute, textAlign: "center", mt: 1 }}
                  >
                    Нажимая кнопку, вы соглашаетесь с обработкой персональных данных
                  </Typography>
                </Stack>
              )}
            </Box>
          </motion.div>
        </Box>
      </Container>
    </Section>
  );
}

function FormField({ label, children }) {
  return (
    <Box>
      <Typography
        level="body-xs"
        sx={{
          color: "rgba(255,255,255,0.6)",
          fontWeight: 600,
          mb: 0.75,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontSize: "0.7rem",
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function SuccessState() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
    >
      <Stack spacing={2} alignItems="center" sx={{ py: 4, textAlign: "center" }}>
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            background: "rgba(52,211,153,0.15)",
            border: "2px solid #34d399",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CheckCircleRoundedIcon sx={{ fontSize: 44, color: "#34d399" }} />
        </Box>
        <Typography level="h3" sx={{ color: "#fff", fontWeight: 800 }}>
          Заявка отправлена
        </Typography>
        <Typography sx={{ color: COLORS.textDim, maxWidth: 360 }}>
          Менеджер перезвонит в течение 5 минут, рассчитает точную стоимость и согласует удобное время выезда.
        </Typography>
      </Stack>
    </motion.div>
  );
}

const fieldSx = {
  background: "rgba(255,255,255,0.06)",
  borderColor: "rgba(255,255,255,0.14)",
  color: "#fff",
  "&:hover": { background: "rgba(255,255,255,0.09)" },
  "&.Mui-focused": { borderColor: COLORS.brand },
  "&::before": { display: "none" },
  "& input::placeholder, & .MuiSelect-placeholder": { color: "rgba(255,255,255,0.35)" },
};